/**
 * LineAttributeModal — edit visual attributes of a connection line.
 *
 * Opened by double-clicking a highlighted connection line on the map.
 * Allows editing line style, color, and optional label.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────

type LineStyle = 'solid' | 'dashed' | 'dotted';

interface LineAttributes {
  lineStyle: LineStyle;
  lineColor: string;
  label: string;
}

interface LineAttributeModalProps {
  /** Display names of the two connected nodes. */
  sourceNodeName: string;
  targetNodeName: string;
  /** Current edge attributes. */
  initial: LineAttributes;
  /** Called with updated attributes on save. */
  onSave: (attrs: LineAttributes) => void;
  onClose: () => void;
}

// ── Constants ────────────────────────────────────────────────────────

const STYLE_OPTIONS: { value: LineStyle; label: string; preview: string }[] = [
  { value: 'solid', label: 'Solid', preview: '————————' },
  { value: 'dashed', label: 'Dashed', preview: '– – – – –' },
  { value: 'dotted', label: 'Dotted', preview: '· · · · · ·' },
];

const COLOR_SWATCHES = [
  { color: '#1e3a5f', label: 'Dark Blue' },
  { color: '#22c55e', label: 'Green' },
  { color: '#f59e0b', label: 'Amber' },
  { color: '#3b82f6', label: 'Blue' },
  { color: '#a78bfa', label: 'Purple' },
  { color: '#ef4444', label: 'Red' },
  { color: '#6b7280', label: 'Gray' },
  { color: '#f1f5f9', label: 'Light' },
];

// ── Component ────────────────────────────────────────────────────────

export function LineAttributeModal({
  sourceNodeName,
  targetNodeName,
  initial,
  onSave,
  onClose,
}: LineAttributeModalProps) {
  const [lineStyle, setLineStyle] = useState<LineStyle>(initial.lineStyle);
  const [lineColor, setLineColor] = useState(initial.lineColor);
  const [label, setLabel] = useState(initial.label);

  const handleSave = useCallback(() => {
    onSave({ lineStyle, lineColor, label: label.trim() });
  }, [lineStyle, lineColor, label, onSave]);

  // Escape to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSave();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, handleSave]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1100]" style={{ background: 'var(--modal-backdrop)' }} onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed top-1/2 left-1/2 z-[1200] w-80 rounded-2xl"
        style={{
          transform: 'translate(-50%, -50%)',
          background: 'var(--modal-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(56, 189, 248, 0.25)',
          boxShadow: 'var(--modal-shadow), 0 0 40px rgba(56,189,248,0.1)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 pb-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Edit Connection
          </h3>
          <button onClick={onClose} className="p-1 rounded" style={{ color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        {/* Nodes */}
        <div className="px-4 pt-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>
          <span style={{ color: 'var(--text-secondary)' }}>{sourceNodeName}</span>
          {' → '}
          <span style={{ color: 'var(--text-secondary)' }}>{targetNodeName}</span>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Line Style */}
          <FieldGroup label="Line Style">
            <div className="flex gap-2">
              {STYLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setLineStyle(opt.value)}
                  className="flex-1 px-2 py-1.5 rounded-lg text-[11px] text-center transition-all"
                  style={{
                    background: lineStyle === opt.value ? 'rgba(56,189,248,0.15)' : 'var(--glass-bg)',
                    border: lineStyle === opt.value ? '1px solid rgba(56,189,248,0.5)' : '1px solid var(--glass-border)',
                    color: lineStyle === opt.value ? '#38bdf8' : 'var(--text-secondary)',
                  }}
                >
                  <div className="text-[10px] mb-0.5" style={{ color: 'var(--text-dim)' }}>{opt.preview}</div>
                  {opt.label}
                </button>
              ))}
            </div>
          </FieldGroup>

          {/* Line Color */}
          <FieldGroup label="Line Color">
            <div className="flex flex-wrap gap-2">
              {COLOR_SWATCHES.map((s) => (
                <button
                  key={s.color}
                  title={s.label}
                  onClick={() => setLineColor(s.color)}
                  className="w-7 h-7 rounded-full transition-all"
                  style={{
                    background: s.color,
                    border: lineColor === s.color ? '2px solid #38bdf8' : '2px solid transparent',
                    outline: lineColor === s.color ? '1px solid rgba(56,189,248,0.3)' : 'none',
                    outlineOffset: '2px',
                  }}
                />
              ))}
            </div>
          </FieldGroup>

          {/* Label */}
          <FieldGroup label="Label (optional)">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. depends on, feeds into..."
              maxLength={40}
              className="w-full px-3 py-1.5 rounded-lg text-[11px]"
              style={{
                background: 'var(--glass-bg)',
                border: '1px solid var(--glass-border)',
                color: 'var(--text-secondary)',
                outline: 'none',
              }}
            />
          </FieldGroup>

          {/* Preview */}
          <FieldGroup label="Preview">
            <svg width="100%" height="24" viewBox="0 0 280 24">
              <line
                x1={10} y1={12} x2={270} y2={12}
                stroke={lineColor}
                strokeWidth={2.5}
                strokeDasharray={
                  lineStyle === 'dashed' ? '8 5' :
                  lineStyle === 'dotted' ? '3 3' :
                  undefined
                }
              />
            </svg>
          </FieldGroup>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
          <button
            onClick={onClose}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-secondary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'rgba(56,189,248,0.2)',
              border: '1px solid rgba(56,189,248,0.4)',
              color: '#38bdf8',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-dim)' }}>
        {label}
      </div>
      {children}
    </div>
  );
}
